import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, Link } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Alert,
  Stack,
  Divider,
  Chip,
  Paper,
} from '@mui/material';

const API_URL = import.meta.env.VITE_API_URL; // p.ej. http://localhost:8000
const POLL_INTERVAL_MS = 3000;   // 3s entre intentos
const POLL_MAX_ATTEMPTS = 15;    // ~45s en total

const mapBackendToUiStatus = (backend) => {
  const s = String(backend || '').trim().toLowerCase();
  // Wompi
  if (s === 'approved') return 'success';
  if (s === 'declined') return 'declined';
  if (s === 'voided' || s === 'error' || s === 'unknown') return 'error';
  if (s === 'pending') return 'pending';
  // Mercado Pago
  if (s === 'rejected') return 'declined';
  if (s === 'cancelled' || s === 'refunded' || s === 'charged_back') return 'error';
  if (s === 'in_process' || s === 'in_mediation') return 'pending';
  return 'loading';
};

const statusLabel = (s) => {
  if (s === 'success') return 'Aprobado';
  if (s === 'declined') return 'Declinado';
  if (s === 'error') return 'Error';
  if (s === 'pending') return 'Pendiente';
  return 'Procesando';
};

const PaymentStatusPage = () => {
  const location = useLocation();
  const [uiStatus, setUiStatus] = useState('loading');
  const [transactionId, setTransactionId] = useState(null);
  const [provider, setProvider] = useState(null); // 'wompi' | 'mercadopago'
  const [txDetails, setTxDetails] = useState(null);
  const [reconcileError, setReconcileError] = useState(null);
  const [isReconciling, setIsReconciling] = useState(false);

  const pollAttemptsRef = useRef(0);
  const pollTimerRef = useRef(null);

  const params = useMemo(() => new URLSearchParams(location.search), [location.search]);

  const getBackendStatus = (data) => data?.status ?? data?.mp_status;

  // 1) Lee parámetros del redirect (Wompi / Mercado Pago)
  useEffect(() => {
    const wompiTxId = params.get('id');
    const mpPaymentId = params.get('collection_id') || params.get('payment_id');

    const nextProvider = wompiTxId ? 'wompi' : mpPaymentId ? 'mercadopago' : null;
    const txId = wompiTxId || mpPaymentId || null;

    setProvider(nextProvider);
    setTransactionId(txId);

    // Status inmediato para UX (si viene en el redirect)
    const rawStatus = params.get('status') || params.get('collection_status') || '';
    const nextUi = mapBackendToUiStatus(rawStatus);

    if (txId) setUiStatus(nextUi === 'loading' ? 'pending' : nextUi);
    else setUiStatus('error');

  }, [params]);

  // 2) Lógica de reconciliación contra backend
  const reconcileOnce = async (txId) => {
    setIsReconciling(true);
    setReconcileError(null);
    try {
      if (!API_URL) throw new Error('Falta VITE_API_URL');
      if (!provider) throw new Error('Proveedor de pago desconocido');

      const base = API_URL.replace(/\/$/, '');
      const path = provider === 'mercadopago' ? '/mercadopago/reconcile' : '/wompi/reconcile';
      const url = `${base}${path}?id=${encodeURIComponent(txId)}`;
      const res = await fetch(url, { method: 'GET' });
      if (!res.ok) {
        const errBody = await res.json().catch(() => ({}));
        throw new Error(errBody?.detail || `Error ${res.status}`);
      }
      const data = await res.json();
      setTxDetails(data);

      // El backend es la “fuente de la verdad”:
      const nextUi = mapBackendToUiStatus(getBackendStatus(data));
      setUiStatus(nextUi);

      return nextUi;
    } catch (e) {
      setReconcileError(e?.message || 'Fallo al reconciliar con el backend.');
      return uiStatus;
    } finally {
      setIsReconciling(false);
    }
  };

  // 3) Polling hasta estado final (success/declined/error) o agotar intentos
  const startPolling = async (txId) => {
    // Si ya hay un timer, no crear otro
    if (pollTimerRef.current) return;

    const tick = async () => {
      if (!txId) return;

      const next = await reconcileOnce(txId);
      pollAttemptsRef.current += 1;

      const isFinal = next === 'success' || next === 'declined' || next === 'error';
      const exhausted = pollAttemptsRef.current >= POLL_MAX_ATTEMPTS;

      if (isFinal || exhausted) {
        if (pollTimerRef.current) {
          window.clearTimeout(pollTimerRef.current);
          pollTimerRef.current = null;
        }
        return;
      }

      // Reintentar
      pollTimerRef.current = window.setTimeout(tick, POLL_INTERVAL_MS);
    };

    // primer intento inmediato
    pollTimerRef.current = window.setTimeout(tick, 0);
  };

  // 4) Al montar o cuando cambie el txId, iniciar reconciliación/polling
  useEffect(() => {
    if (!transactionId) return;
    pollAttemptsRef.current = 0;
    if (pollTimerRef.current) {
      window.clearTimeout(pollTimerRef.current);
      pollTimerRef.current = null;
    }
    startPolling(transactionId);

    // Limpieza al desmontar
    return () => {
      if (pollTimerRef.current) {
        window.clearTimeout(pollTimerRef.current);
        pollTimerRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [transactionId]);

  // 5) Acciones manuales
  const handleManualReconcile = async () => {
    if (!transactionId) return;
    await reconcileOnce(transactionId);
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '80vh',
        textAlign: 'center',
        p: 3,
      }}
    >
      {/* Encabezado */}
      <Stack spacing={1} sx={{ mb: 3, alignItems: 'center' }}>
        <Typography variant="h4" sx={{ fontWeight: 700 }}>
          Estado del Pago
        </Typography>
        <Chip
          label={statusLabel(uiStatus)}
          color={
            uiStatus === 'success' ? 'success'
            : uiStatus === 'declined' ? 'warning'
            : uiStatus === 'error' ? 'error'
            : 'info'
          }
          variant="filled"
          sx={{ fontWeight: 600 }}
        />
      </Stack>

      {/* Cargas y estados */}
      {(uiStatus === 'loading' || isReconciling) && (
        <Stack spacing={2} sx={{ alignItems: 'center', mb: 3 }}>
          <CircularProgress />
          <Typography variant="body1">Procesando tu pago...</Typography>
        </Stack>
      )}

      {uiStatus === 'success' && (
        <Stack spacing={2} sx={{ alignItems: 'center', mb: 3 }}>
          <Typography variant="h5" color="success.main" fontWeight={700}>
            ¡Pago Aprobado!
          </Typography>
          <Typography variant="body1">
            Tu saldo ha sido actualizado. Gracias por tu recarga.
          </Typography>
        </Stack>
      )}

      {uiStatus === 'declined' && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          Tu pago fue declinado. Por favor, intenta con otro método de pago.
        </Alert>
      )}

      {uiStatus === 'error' && (
        <Alert severity="error" sx={{ mb: 3 }}>
          Ocurrió un error al procesar tu pago. Por favor, contacta a soporte.
        </Alert>
      )}

      {uiStatus === 'pending' && (
        <Alert severity="info" sx={{ mb: 3 }}>
          Tu pago está pendiente. Te notificaremos cuando se complete.
        </Alert>
      )}

      {/* Detalles de la transacción (si existen) */}
      {txDetails && (
        <Paper
          elevation={3}
          sx={{
            p: 2.5,
            borderRadius: 3,
            minWidth: { xs: '100%', sm: 480 },
            textAlign: 'left',
            mb: 3,
          }}
        >
          <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
            Detalles de la Transacción
          </Typography>
          <Divider sx={{ mb: 2 }} />
          <Stack spacing={1.25}>
            {provider === 'mercadopago' ? (
              <>
                <Typography variant="body2"><strong>Payment ID:</strong> {txDetails.payment_id}</Typography>
                <Typography variant="body2"><strong>Estado:</strong> {txDetails.mp_status}</Typography>
                {typeof txDetails.credited !== 'undefined' && (
                  <Typography variant="body2"><strong>Acreditado:</strong> {txDetails.credited ? 'Sí' : 'No'}</Typography>
                )}
              </>
            ) : (
              <>
                <Typography variant="body2"><strong>ID:</strong> {txDetails.transaction_id}</Typography>
                <Typography variant="body2"><strong>Estado:</strong> {txDetails.status}</Typography>
                <Typography variant="body2"><strong>Monto:</strong> ${txDetails.amount?.toLocaleString('es-CO')} COP</Typography>
                {typeof txDetails.reference !== 'undefined' && (
                  <Typography variant="body2"><strong>Referencia:</strong> {txDetails.reference}</Typography>
                )}
              </>
            )}
          </Stack>
        </Paper>
      )}

      {/* Error de reconciliación (si ocurre) */}
      {reconcileError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {reconcileError}
        </Alert>
      )}

      {/* Acciones */}
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
        <Button
          component={Link}
          to="/mi-billetera"
          variant="contained"
          sx={{ minWidth: 200 }}
        >
          Volver a Mi Billetera
        </Button>

        {transactionId && (
          <Button
            onClick={handleManualReconcile}
            variant="outlined"
            disabled={isReconciling}
            sx={{ minWidth: 200 }}
          >
            Reconciliar ahora
          </Button>
        )}
      </Stack>
    </Box>
  );
};

export default PaymentStatusPage;